import { createAction, props } from '@ngrx/store';

import { DatablockListEntity } from '../components/datablock-list/datablock-list.entity';

interface IPersistenceInfo {
	isPersisted: boolean;
	partitionCount: number;
	persistenceDate: Date | null;
	persistenceDuration: number;
	persistenceSize: number;
}

export enum DatablockActions {
	InitDatablockList = '[Datablock] get datablock list',
	RefreshDatablockList = '[BE][Datablock] refresh datablock list',
	FetchDatablockListFail = '[BE][Datablock] fetch datablock list fail',
	FetchDatablockListSuccess = '[BE][Datablock] fetch datablock list success',
	ConstructDatablockList = '[Datablock] Construct datablock list',
	ConstructDatablockListSuccess = '[BE][Datablock] Construct datablock list success',
	ConstructDatablockListFail = '[BE][Datablock] Construct datablock list fail',
	NavigateToDatablockCreateRoute = '[Datablock] navigate to datablock create route',
	NavigateToDatablockRoute = '[Datablock] navigate to datablock route',
	NavigateToDatablockDuplicateRoute = '[Datablock] navigate to datablock duplicate route',
	NavigateToCreateFromDatablockRoute = '[Datablock] navigate to create from datablock route',
	NavigateToExportDatablockRoute = '[Datablock] navigate to export datablock route',
	DeleteDatablock = '[Datablock] delete datablock',
	DeleteDatablockSuccess = '[BE][Datablock] delete datablock success',
	DeleteDatablockFail = '[BE][Datablock] delete datablock fail',
	PersistDatablocks = '[Datablock] persist datablocks',
	PersistDatablockStarted = '[Datablock] persist datablock started',
	PersistDatablockStopped = '[Datablock] persist datablock stopped',
	PersistDatablockFail = '[BE][Datablock] persist datablock fail',
	UnpersistDatablocks = '[Datablock] unpersist datablocks',
	UnpersistDatablockStarted = '[Datablock] unpersist datablock started',
	UnpersistDatablockStopped = '[Datablock] unpersist datablock stopped',
	UnpersistDatablockFail = '[BE][Datablock] unpersist datablock fail',
	RepersistDatablocks = '[Datablock] repersist datablocks',
	RepersistDatablockStarted = '[Datablock] repersist datablock started',
	RepersistDatablockStopped = '[Datablock] repersist datablock stopped',
	RepersistDatablockFail = '[BE][Datablock] repersist datablock fail',
	BulkDelete = '[Datablock] delete many datablocks',
	BulkDeleteSuccess = '[BE][Datablock] delete many datablocks success',
	BulkDeleteFail = '[BE][Datablock] delete many datablocks fail',
	PersistanceActionFinish = '[Datablock] persistence action finished',
	PersistenceActionsAccepted = '[Datablock] persistence actions accepted',
	TrackDatablockAction = '[BE][Datablock] track datablock action',
	ToggleDatablockStatus = '[Datablock] toggle datablock status',
	StopPersistence = '[Datablock] stop persistence',
	StopPersistenceFail = '[Datablock] stop persistence fail',
	UpdateDatablocksExpositionsCount = '[Datablock] update datablocks expositions count',
	UpdateDatablocksExpositionsCountSuccess = '[Datablock] update datablocks expositions count success',
	UpdateDatablocksExpositionsCountFailure = '[Datablock] update datablocks expositions count failure',
}

export const initDatablockList = createAction(
	DatablockActions.InitDatablockList
);

export const refreshDatablockList = createAction(
	DatablockActions.RefreshDatablockList
);

export const fetchDatablockListSuccess = createAction(
	DatablockActions.FetchDatablockListSuccess,
	props<{ datablockList: DatablockListEntity }>()
);

export const fetchDatablockListFail = createAction(
	DatablockActions.FetchDatablockListFail,
	props<{ error: unknown }>()
);

export const constructDatablockList = createAction(
	DatablockActions.ConstructDatablockList
);

export const constructDatablockListSuccess = createAction(
	DatablockActions.ConstructDatablockListSuccess,
	props<{ datablockList: DatablockListEntity }>()
);

export const constructDatablockListFail = createAction(
	DatablockActions.ConstructDatablockListFail,
	props<{ error: unknown }>()
);

export const navigateToDatablock = createAction(
	DatablockActions.NavigateToDatablockRoute,
	props<{ id: number }>()
);
export const navigateToDatablockDuplicate = createAction(
	DatablockActions.NavigateToDatablockDuplicateRoute,
	props<{ id: number }>()
);
export const navigateToDatablockCreate = createAction(
	DatablockActions.NavigateToDatablockCreateRoute
);
export const navigateToCreateFromDatablock = createAction(
	DatablockActions.NavigateToCreateFromDatablockRoute,
	props<{ id: number }>()
);

export const navigateToExportDatablock = createAction(
	DatablockActions.NavigateToExportDatablockRoute,
	props<{ id: number; label: string }>()
);

export const deleteDatablock = createAction(
	DatablockActions.DeleteDatablock,
	props<{ datablockId: number }>()
);

export const bulkDelete = createAction(
	DatablockActions.BulkDelete,
	props<{ datablockId: Array<number> }>()
);

export const deleteDatablockSuccess = createAction(
	DatablockActions.DeleteDatablockSuccess
);

export const deleteDatablockFail = createAction(
	DatablockActions.DeleteDatablockFail
);

export const bulkDeleteSuccess = createAction(
	DatablockActions.BulkDeleteSuccess
);

export const bulkDeleteFail = createAction(DatablockActions.BulkDeleteFail);

export const persistDatablocks = createAction(
	DatablockActions.PersistDatablocks,
	props<{ datablockIds: Array<number> }>()
);

export const persistDatablockStarted = createAction(
	DatablockActions.PersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const persistDatablockStopped = createAction(
	DatablockActions.PersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const unpersistDatablocks = createAction(
	DatablockActions.UnpersistDatablocks,
	props<{ datablockIds: Array<number> }>()
);

export const unpersistDatablockStarted = createAction(
	DatablockActions.UnpersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const unpersistDatablockStopped = createAction(
	DatablockActions.UnpersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablocks = createAction(
	DatablockActions.RepersistDatablocks,
	props<{ datablockIds: Array<number> }>()
);

export const repersistDatablockStarted = createAction(
	DatablockActions.RepersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablockStopped = createAction(
	DatablockActions.RepersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const persistenceActionFinish = createAction(
	DatablockActions.PersistanceActionFinish,
	props<{
		datablockId: number;
		persistenceInfo: IPersistenceInfo;
	}>()
);

export const persistenceActionsAccepted = createAction(
	DatablockActions.PersistenceActionsAccepted
);

export const trackAction = createAction(
	DatablockActions.TrackDatablockAction,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablockFail = createAction(
	DatablockActions.RepersistDatablockFail,
	props<{ error: unknown }>()
);
export const unpersistDatablockFail = createAction(
	DatablockActions.UnpersistDatablockFail,
	props<{ error: unknown }>()
);
export const persistDatablockFail = createAction(
	DatablockActions.PersistDatablockFail,
	props<{ error: unknown }>()
);

export const toggleDatablockStatus = createAction(
	DatablockActions.ToggleDatablockStatus,
	props<{ datablockId: number }>()
);

export const stopPersistence = createAction(
	DatablockActions.StopPersistence,
	props<{ jobKey: string }>()
);

export const stopPersistenceFail = createAction(
	DatablockActions.StopPersistenceFail,
	props<{ error: unknown }>()
);

export const updateDatablocksExpositionsCount = createAction(
	DatablockActions.UpdateDatablocksExpositionsCount,
	props<{ ids: Array<number>; shouldReload: boolean }>()
);

export const updateDatablocksExpositionsCountSuccess = createAction(
	DatablockActions.UpdateDatablocksExpositionsCountSuccess,
	props<{ datablockList: DatablockListEntity }>()
);

export const updateDatablocksExpositionsCountFailure = createAction(
	DatablockActions.UpdateDatablocksExpositionsCountFailure,
	props<{ error: unknown }>()
);
